<template>
	<v-row no-gutters class="pa-2 overflow-auto" style="height: 100%">
		<!-- Time tracking of the user in this page -->
		<UserTimeTracker page-key="ccmCondition" />

		<v-col cols="12" class="pa-1" style="height: 100%">
			<v-card style="height: 100%; min-height: 300px" :loading="dataLoading">
				<v-card-title class="py-2 py-lg-4">
					<v-row no-gutters>
						<v-col class="text-left" :class="$vuetify.breakpoint.mdAndDown ? 'font-responsive-md' : ''">
							{{ $t('conditions') }}
						</v-col>
						<v-spacer />
						<v-col class="text-right">
							<ActionButton
								icon="mdi-file-delimited"
								tooltip="download-csv"
								:maxSize="25"
								:minSize="20"
								@clicked="downloadCSVOfConditions()"
								:busy="downloadingIds.includes(-1)" />
							<v-icon
								color="primary"
								@click="synchronizeAddDrawers('ccmConditionAdd')"
								:size="$vuetify.breakpoint.mdAndDown ? 20 : 25">
								mdi-plus-circle
							</v-icon>
						</v-col>
					</v-row>
				</v-card-title>
				<v-card-text
					class="overflow-auto"
					:style="$vuetify.breakpoint.lgAndUp ? 'height: calc(100% - 64px)' : 'height: calc(100% - 48px)'">
					<v-data-table
						:items="ccmConditions"
						:headers="mainHeaders"
						:items-per-page="-1"
						:height="pageHeight($vuetify.breakpoint.mdAndDown ? 93 : 77)"
						fixed-header
						single-expand
						:key="key"
						dense
						class="data-table-outline overflow-y-auto"
						:footer-props="{ 'items-per-page-text': '' }">
						<template v-slot:item="{ item, expand, isExpanded, index }">
							<tr @click.stop="openViewDialog(item)" :class="{ ' odd-row': index % 2, 'expanded-row': isExpanded }">
								<td class="text-left px-1">
									{{ item.name.split(' : ')[0] }}
								</td>
								<td class="text-left px-1">{{ item.severity }}</td>
								<td class="text-left px-1">
									<v-chip
										v-if="item.isPrimary"
										color="primary"
										:small="$vuetify.breakpoint.lgAndUp"
										:x-small="$vuetify.breakpoint.mdAndDown">
										{{ $t('primary') }}
									</v-chip>
								</td>
								<td class="text-left px-1">
									{{ formatTimestamp(item.conditionTimestampUTC) }}
								</td>
								<td class="px-1 text-right">
									<ActionButton
										v-if="item.ccmRecordId === selectedCCMRecord.id"
										icon="mdi-delete"
										tooltip="delete"
										:maxSize="25"
										:minSize="20"
										@clicked="
											deletingHistory = false;
											deleteId = item.id;
											showDeleteDialog = true;
										"
										:busy="deletingConditionIds.includes(item.id)" />

									<v-tooltip left v-if="item.ccmRecordId === selectedCCMRecord.id">
										<template v-slot:activator="{ on }">
											<v-icon
												class="mt-1"
												color="primary"
												:size="$vuetify.breakpoint.mdAndDown ? 20 : 25"
												v-on="on"
												@click.stop="synchronizeEditDrawers('ccmConditionEdit', item)">
												mdi-pencil
											</v-icon>
										</template>
										<span>{{ $t('edit') }}</span>
									</v-tooltip>

									<ActionButton
										icon="mdi-file-delimited"
										tooltip="download-csv"
										:maxSize="25"
										:minSize="20"
										@clicked="downloadCSVofConditionHistory(item)"
										:busy="downloadingIds.includes(item.id)" />
								</td>
								<td class="text-right px-1" @click.stop="expand(!isExpanded)">
									<v-icon color="primary">
										{{ isExpanded ? 'mdi-menu-up' : 'mdi-menu-down' }}
									</v-icon>
								</td>
							</tr>
						</template>
						<template v-slot:expanded-item="{ headers, item: parentCondition }">
							<td :colspan="headers.length" class="pa-3">
								<v-data-table
									:items="parentCondition.conditionHistory"
									:headers="historyHeaders"
									:items-per-page="-1"
									:no-data-text="$t('no-data-available')"
									style="overflow-y: auto; width: 100%"
									:height="pageHeight(-35)"
									fixed-header
									hide-default-footer
									dense
									class="data-table-outline">
									<template v-slot:item="{ item, index }">
										<tr :class="index % 2 ? 'odd-row' : ''" @click.stop="openHistoryViewDialog(item)">
											<td class="text-left px-1">
												{{ item.eventType }}
											</td>
											<td class="text-left px-1 truncate-text" style="max-width: 20vw">
												{{ item.reason }}
											</td>
											<td class="text-left px-1">
												{{ item.performedBy }}
											</td>
											<td class="text-left px-1">
												{{ formatTimestamp(item.conditionHistoryTimestampUTC, 'datetime_med') }}
											</td>
											<td class="px-1 text-right">
												<ActionButton
													v-if="parentCondition.ccmRecordId === selectedCCMRecord.id"
													icon="mdi-delete"
													tooltip="delete"
													:maxSize="25"
													:minSize="20"
													@clicked="
														deletingHistory = true;
														deleteId = item.id;
														showDeleteDialog = true;
													"
													:busy="deletingHistoryIds.includes(item.id)" />

												<v-tooltip left v-if="parentCondition.ccmRecordId === selectedCCMRecord.id">
													<template v-slot:activator="{ on }">
														<v-icon
															class="mt-1"
															color="primary"
															:size="$vuetify.breakpoint.mdAndDown ? 20 : 25"
															v-on="on"
															@click.stop="synchronizeEditDrawers('ccmConditionHistoryEdit', item, true)">
															mdi-pencil
														</v-icon>
													</template>
													<span>{{ $t('edit') }}</span>
												</v-tooltip>
											</td>
										</tr>
									</template>
								</v-data-table>
							</td>
						</template>
						<template v-slot:[`footer.prepend`]>
							<v-row dense class="ma-0">
								<v-col class="text-left">
									<p class="ma-0">
										<v-tooltip top>
											<template v-slot:activator="{ on }">
												<v-icon class="ml-1" color="primary" size="25" v-on="on"> mdi-help-circle-outline </v-icon>
											</template>
											{{ $t('need-to-select-the-correct-record') }}
										</v-tooltip>
									</p>
								</v-col>
							</v-row>
						</template>
					</v-data-table>
				</v-card-text>
			</v-card>
		</v-col>
		<CCMConditionHistoryViewDialog
			:dialog.sync="showHistoryViewDialog"
			v-if="showHistoryViewDialog && selectedCCMConditionHistory"
			:selectedConditionHistory="selectedCCMConditionHistory"
			@closeDialog="showHistoryViewDialog = false" />
		<CCMConditionViewDialog
			:dialog.sync="showViewDialog"
			v-if="showViewDialog && selectedCCMCondition"
			:selectedCondition="selectedCCMCondition"
			@closeDialog="showViewDialog = false" />

		<DeleteDialog
			:dialog.sync="showDeleteDialog"
			@confirm="
				showDeleteDialog = false;
				deletingHistory ? deletingHistoryIds.push(deleteId) : deletingConditionIds.push(deleteId);
				deletingHistory ? deleteHistory(deleteId) : deleteConditionEvent(deleteId);
				deleteId = -1;
			" />
	</v-row>
</template>

<script>
import { mapActions, mapState, mapGetters, mapMutations } from 'vuex';
import {
	getRowsForDataTable,
	formatTimestamp,
	formatDuration,
	downloadCSVWithKeys,
	getFullName,
} from '@/utils/utilities';
import CCMConditionViewDialog from '@/views/dialogs/CCMConditionViewDialog.vue';
import CCMConditionHistoryViewDialog from '@/views/dialogs/CCMConditionHistoryViewDialog.vue';
import DeleteDialog from '@/views/dialogs/DeleteDialog.vue';
import UserTimeTracker from '@/views/Client/components/UserTimeTracker.vue';
import ActionButton from '../ActionButton.vue';

export default {
	name: 'CCMConditions',

	components: {
		CCMConditionViewDialog,
		CCMConditionHistoryViewDialog,
		DeleteDialog,
		UserTimeTracker,
		ActionButton,
	},

	data () {
		return {
			dataLoading: true,
			key: 0,
			showHistoryViewDialog: false,
			showViewDialog: false,
			selectedCCMCondition: null,
			selectedCCMConditionHistory: null,
			deleteId: -1,
			deletingConditionIds: [],
			deletingHistoryIds: [],
			deletingHistory: false,
			showDeleteDialog: false,
			downloadingIds: [],
		};
	},

	computed: {
		...mapState({
			detailedUser: (state) => state.users.detailedUser,
			ccmConditions: (state) => state.ccmConditions.ccmConditionsByPatientId,
			selectedCCMRecord: (state) => state.ccmRecords.ccmRecord,
		}),
		...mapGetters({
			pageHeight: 'pageHeight',
		}),
		historyHeaders () {
			return [
				{
					text: this.$t('event-type'),
					value: 'eventType',
					class: 'px-1',
				},
				{
					text: this.$t('reason'),
					value: 'reason',
					class: 'px-1',
					sortable: false,
				},
				{
					text: this.$t('staff'),
					value: 'performedBy',
					class: 'px-1',
				},
				{
					text: this.$t('change-timestamp'),
					value: 'conditionHistoryTimestampUTC',
					class: 'px-1',
				},
				{
					text: this.$t('actions'),
					sortable: false,
					align: 'right',
					class: 'pl-1 pr-3',
				},
			];
		},
		mainHeaders () {
			return [
				{
					text: this.$t('condition-name'),
					value: 'name',
					class: 'px-1',
				},
				{
					text: this.$t('severity'),
					value: 'severity',
					class: 'px-1',
				},
				{
					text: '',
					value: 'isPrimary',
					class: 'px-1',
				},
				{
					text: this.$t('date'),
					value: 'conditionTimestampUTC',
					class: 'px-1',
				},
				{
					text: this.$t('actions'),
					sortable: false,
					align: 'right',
					class: 'pl-1 pr-3',
				},
				{
					text: '',
					value: 'data-table-expand',
					sortable: false,
					class: 'px-1',
				},
			];
		},
	},

	methods: {
		...mapMutations({
			storeConditionHistory: 'ccmConditions/STORE_CCM_CONDITION_HISTORY',
		}),
		...mapActions({
			deleteCondition: 'ccmConditions/deleteCCMCondition',
			deleteConditionHistory: 'ccmConditions/deleteConditionHistory',
			getCCMConditions: 'ccmConditions/getCCMConditionsByPatientId',
			getSelectedCCMCondition: 'ccmConditions/getCCMCondition',
			storeCCMRecordsByPatientId: 'ccmRecords/storeMinimalCCMRecordsByPatientId',
		}),

		getRowsForDataTable,
		formatDuration,
		formatTimestamp,
		downloadCSVWithKeys,
		getFullName,

		async deleteHistory (historyId) {
			await this.deleteConditionHistory(historyId);
			await this.getCCMConditions(this.detailedUser.userData.id);
			await this.storeCCMRecordsByPatientId(this.detailedUser.userData.id);
			this.deletingHistoryIds = this.deletingHistoryIds.filter((id) => id !== historyId);
		},
		async deleteConditionEvent (conditionId) {
			await this.deleteCondition(conditionId);
			await this.storeCCMRecordsByPatientId(this.detailedUser.userData.id);
			this.deletingConditionIds = this.deletingConditionIds.filter((id) => id !== conditionId);
		},
		async openHistoryViewDialog (conditionHistory) {
			this.selectedCCMConditionHistory = conditionHistory;
			this.showHistoryViewDialog = true;
		},
		async openViewDialog (condition) {
			this.selectedCCMCondition = condition;
			this.showViewDialog = true;
		},
		parseReason (reason) {
			return JSON.parse(reason).reason;
		},
		async downloadCSVOfConditions () {
			this.downloadingIds.push(-1);
			await downloadCSVWithKeys(this.ccmConditions, 'Conditions_for_' + getFullName(this.detailedUser.userData), {
				ids: ['ccmRecordId'],
				data: ['icD10Code', 'isPrimary', 'severity', 'name', 'managementGoals', 'staffName'],
				timestamps: ['conditionTimestampUTC'],
				timeSpent: ['timeSpentSeconds'],
			});
			this.downloadingIds = this.downloadingIds.filter((item) => item !== -1);
		},
		async downloadCSVofConditionHistory (item) {
			this.downloadingIds.push(item.id);
			await downloadCSVWithKeys(
				item.conditionHistory,
				'Condition_' + item.id + '_History_for_' + getFullName(this.detailedUser.userData),
				{
					ids: [],
					data: ['eventType', 'reason', 'performedBy', 'performedByRole'],
					timestamps: ['conditionHistoryTimestampUTC'],
					timeSpent: ['timeSpentSeconds'],
				},
			);
			this.downloadingIds = this.downloadingIds.filter((id) => id !== item.id);
		},
		synchronizeAddDrawers (drawer) {
			const parameters = {
				detailsDrawer: true,
				page: drawer,
			};
			this.$store.commit('SET_DETAILS_DRAWER', parameters);
		},
		async synchronizeEditDrawers (drawer, item, changingHistory = false) {
			changingHistory ? await this.storeConditionHistory(item) : await this.getSelectedCCMCondition(item.id);
			this.$store.commit('SET_PATIENT_DETAILS_DRAWER', drawer);
			this.$store.commit('SET_UPDATE_DETAILS_DRAWER', {
				updateDrawer: true,
				page2: 'ccmDetails',
			});
		},
	},

	async mounted () {
		await this.getCCMConditions(this.detailedUser.userData.id);
		this.dataLoading = false;
	},

	watch: {
		selectedCCMRecord () {
			this.key = !this.key;
		},
	},
};
</script>
